import { calculateXMicros, calculateXValue } from './ranking';
import type { LeaderboardBoard, LeaderboardEntry, LeaderboardSeasonId } from './types';

const BET_CENTS = new Set([10, 20, 50, 100, 200, 500, 600, 700, 800, 900, 1_000, 2_000]);
type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function cleanName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const result = value.replace(/\s+/g, ' ').trim().slice(0, 20);
  return result || null;
}

function cleanId(value: unknown, fallback?: string): string | null {
  const candidate = typeof value === 'string' && value.trim() ? value.trim() : fallback;
  return candidate && /^[A-Za-z0-9_-]{1,128}$/.test(candidate) ? candidate : null;
}

function positiveInteger(value: unknown): number | null {
  return Number.isSafeInteger(value) && Number(value) > 0 ? Number(value) : null;
}

export interface LeaderboardDecodeContext {
  readonly board: LeaderboardBoard;
  readonly seasonId: LeaderboardSeasonId;
  readonly fallbackId?: string;
}

export function decodeLeaderboardEntry(value: unknown, context: LeaderboardDecodeContext): LeaderboardEntry | null {
  const source = record(value);
  if (!source) return null;
  const clientEntryId = cleanId(source.clientEntryId ?? source.id, context.fallbackId);
  const entryId = cleanId(source.id, context.fallbackId ?? clientEntryId ?? undefined);
  const name = cleanName(source.name ?? source.username);
  const createdAt = positiveInteger(source.createdAt ?? source.timestamp);
  let betCents = positiveInteger(source.betCents);
  let profitCents = positiveInteger(source.profitCents);
  if (betCents === null && typeof source.bet === 'number' && Number.isFinite(source.bet)) betCents = Math.round(source.bet * 100);
  const legacyProfit = source.profit ?? source.win;
  if (profitCents === null && typeof legacyProfit === 'number' && Number.isFinite(legacyProfit)) profitCents = Math.round(legacyProfit * 100);
  if (!clientEntryId || !entryId || !name || createdAt === null || betCents === null || !BET_CENTS.has(betCents)
    || profitCents === null || profitCents <= 0) return null;
  const entry: LeaderboardEntry = {
    id: entryId, clientEntryId, name, betCents, profitCents, createdAt,
    seasonId: context.seasonId, board: context.board,
    ...(typeof source.uid === 'string' && source.uid ? { ownerUid: source.uid } : {}),
    ...(typeof source.replayAvailable === 'boolean' ? { replayAvailable: source.replayAvailable } : {}),
  };
  const claimedMicros = positiveInteger(source.xMicros);
  const claimedValue = typeof source.xValue === 'number' && Number.isFinite(source.xValue) ? source.xValue : null;
  const exactMicros = calculateXMicros(entry);
  if (claimedMicros !== null && Math.abs(claimedMicros - exactMicros) > 1) return null;
  const legacyRoundingTolerance = claimedMicros === null ? 0.06 : 0.000_01;
  if (claimedValue !== null && Math.abs(claimedValue - calculateXValue(entry)) > legacyRoundingTolerance) return null;
  return Object.freeze(entry);
}

export function buildRemoteLeaderboardEntry(entry: LeaderboardEntry, uid: string): Readonly<Record<string, unknown>> {
  return Object.freeze({
    seasonId: 'season3',
    uid,
    clientEntryId: entry.clientEntryId,
    name: entry.name,
    username: entry.name,
    betCents: entry.betCents,
    profitCents: entry.profitCents,
    xMicros: calculateXMicros(entry),
    xValue: Number(calculateXValue(entry).toFixed(6)),
    createdAt: entry.createdAt,
    // Legacy readers continue to understand the same entries.
    bet: entry.betCents / 100,
    profit: entry.profitCents / 100,
    timestamp: entry.createdAt,
  });
}
